"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "@/i18n/navigation";
import { AnnouncementBar } from "@/components/announcement-bar";
import { Navbar } from "@/components/navbar";
import { Footer } from "@/components/footer";
import { WhatsAppButton } from "@/components/whatsapp-button";
import { MobileTabBar } from "@/components/mobile-tab-bar";
import { useTranslations } from "next-intl";
import { faqs as defaultFaqs, type FAQSection } from "@/lib/faq-data";

function FAQItem({ q, a }: { q: string; a: string }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="border-b border-border">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-start justify-between gap-4 py-5 text-left"
      >
        <span className="text-sm font-medium text-foreground">{q}</span>
        <svg
          className={`w-4 h-4 text-text-tertiary shrink-0 mt-0.5 transition-transform ${open ? "rotate-180" : ""}`}
          style={{ transitionDuration: "var(--duration-fast)" }}
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <path d="M6 9l6 6 6-6" />
        </svg>
      </button>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="pb-5 text-sm text-text-body leading-relaxed pr-8"
        >
          {a}
        </motion.div>
      )}
    </div>
  );
}

export function FAQPage({ faqs }: { faqs?: FAQSection[] }) {
  const t = useTranslations("faq");
  const tFaq = useTranslations("faqPage");
  const tCommon = useTranslations("common");
  // Server-rendered policy-aware FAQs when the route passes them in.
  // Falls back to the platform-default static set so the legacy import
  // path (without props) keeps working.
  const sections = faqs ?? defaultFaqs;
  return (
    <>
      <AnnouncementBar />
      <Navbar />
      <main className="flex-1">
        <div className="content-wrapper py-12 lg:py-20">
          <nav className="flex items-center gap-1.5 text-xs text-text-tertiary mb-8">
            <Link href="/" className="hover:text-foreground transition-colors">{tCommon("home")}</Link>
            <span>/</span>
            <span className="text-text-secondary">FAQ</span>
          </nav>

          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-12">
              <p className="label-sm mb-3">{t("title")}</p>
              <h1 className="heading-lg mb-4">{t("howCanWeHelp")}</h1>
              <p className="text-text-body text-sm leading-relaxed">
                {tFaq.rich("notListedTop", {
                  link: (chunks) => (
                    <Link href="/contact" className="text-foreground underline">{chunks}</Link>
                  ),
                })}
              </p>
            </div>

            <div className="space-y-10">
              {sections.map((section) => (
                <div key={section.category}>
                  <h2 className="label-sm mb-4">{section.category}</h2>
                  <div>
                    {section.items.map((item) => (
                      <FAQItem key={item.q} q={item.q} a={item.a} />
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Contact CTA */}
            <div className="mt-12 pt-8 border-t border-border text-center">
              <p className="text-sm text-text-body mb-4">
                {t("questionNotListedBottom")}
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link
                  href="/contact"
                  className="inline-flex items-center justify-center h-11 px-6 bg-foreground text-background label-sm hover:bg-foreground/85 transition-colors rounded-lg"
                  style={{ transitionDuration: "var(--duration-fast)", transitionTimingFunction: "var(--ease-out)" }}
                >
                  {t("contactCta")}
                </Link>
                <a
                  href="https://wa.me/31657222000"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center h-11 px-6 border border-foreground/20 text-foreground label-sm hover:border-foreground/40 transition-colors rounded-lg"
                  style={{ transitionDuration: "var(--duration-fast)", transitionTimingFunction: "var(--ease-out)" }}
                >
                  WhatsApp
                </a>
              </div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
      <WhatsAppButton />
      <MobileTabBar />
    </>
  );
}
